(() => {
  const initializedForms = new WeakSet();
  const initializedStickyBars = new WeakSet();
  const initializedAvailability = new WeakSet();

  const validateRequiredField = (field, showMessage = false) => {
    const error = field
      .closest('.jw-service-field')
      ?.querySelector('[data-service-field-error]');
    const invalid = !field.validity.valid;

    field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    if (error) error.hidden = !invalid || !showMessage;
    return !invalid;
  };

  const initializeServiceForm = (productForm) => {
    if (initializedForms.has(productForm)) return;
    initializedForms.add(productForm);

    const form = productForm.querySelector('form');
    if (!form) return;

    const requiredFields = Array.from(form.querySelectorAll('[data-service-required]'));
    requiredFields.forEach((field) => {
      field.addEventListener('blur', () => validateRequiredField(field, true));
      field.addEventListener('input', () => {
        if (field.getAttribute('aria-invalid') === 'true') validateRequiredField(field, true);
      });
    });

    form.addEventListener(
      'submit',
      (event) => {
        const firstInvalid = requiredFields.find((field) => !validateRequiredField(field, true));
        if (!firstInvalid) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        firstInvalid.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        firstInvalid.focus({ preventScroll: true });
        firstInvalid.reportValidity();
      },
      true
    );
  };

  const initializeServiceAvailability = (availability) => {
    if (initializedAvailability.has(availability)) return;
    initializedAvailability.add(availability);

    const root = availability.closest('product-info');
    const sectionId = root?.dataset.section;
    const sourceButton = sectionId ? document.getElementById(`ProductSubmitButton-${sectionId}`) : null;
    const availabilityText = availability.querySelector('[data-service-availability-text]');
    const contactLink = availability.querySelector('[data-service-contact-link]');
    if (!sourceButton || !availabilityText) return;

    const sync = () => {
      const unavailable = sourceButton.disabled;
      availability.classList.toggle('is-unavailable', unavailable);
      availabilityText.textContent = unavailable
        ? 'Aktuell nicht online beauftragbar'
        : 'Service online beauftragbar';
      if (contactLink) contactLink.hidden = !unavailable;
    };

    const observer = new MutationObserver(sync);
    observer.observe(sourceButton, { attributes: true, attributeFilter: ['disabled'] });
    sync();
  };

  const initializeStickyBar = (sticky) => {
    if (initializedStickyBars.has(sticky)) return;
    initializedStickyBars.add(sticky);

    const root = sticky.closest('product-info');
    const sectionId = sticky.dataset.sectionId;
    const form = document.getElementById(sticky.dataset.formId);
    const sourceButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const sourcePrice = document.getElementById(`price-${sectionId}`);
    const stickyButton = sticky.querySelector('[data-service-sticky-button]');
    const stickyPrice = sticky.querySelector('[data-service-sticky-price]');

    if (!root || !form || !sourceButton || !stickyButton) return;

    const sync = () => {
      if (sourcePrice && stickyPrice) stickyPrice.innerHTML = sourcePrice.innerHTML;

      const sourceLabel = sourceButton.querySelector('span')?.textContent.trim();
      const unavailable = sourceButton.disabled;
      const busy = sourceButton.getAttribute('aria-disabled') === 'true';

      stickyButton.disabled = unavailable || busy;
      if (sourceLabel) stickyButton.textContent = sourceLabel;

    };

    stickyButton.addEventListener('click', () => {
      if (stickyButton.disabled) return;
      form.requestSubmit(sourceButton);
    });

    const sourceObserver = new MutationObserver(sync);
    sourceObserver.observe(sourceButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
      characterData: true,
    });
    if (sourcePrice) sourceObserver.observe(sourcePrice, { childList: true, subtree: true, characterData: true });

    let sourceButtonVisible = true;
    const updateStickyVisibility = () => {
      const visible = window.scrollY > 280 && !sourceButtonVisible;
      sticky.classList.toggle('is-visible', visible);
      sticky.toggleAttribute('inert', !visible);
      sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
    };

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        sourceButtonVisible = entry.isIntersecting;
        updateStickyVisibility();
      },
      { threshold: 0.15 }
    );
    intersectionObserver.observe(sourceButton);
    window.addEventListener('scroll', updateStickyVisibility, { passive: true });

    sync();
    updateStickyVisibility();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('product-form[data-service-product="true"]').forEach(initializeServiceForm);
    scope.querySelectorAll?.('[data-service-availability]').forEach(initializeServiceAvailability);
    scope.querySelectorAll?.('[data-service-sticky]').forEach(initializeStickyBar);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('product-info:loaded', (event) => initialize(event.target));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
